import { isEqual } from 'lodash'
import { reaction } from 'mobx'
import { useEffect, useRef, useState } from 'react'
import type { UserSetting } from 'app/types/entities'
import { useUserSettingStore } from 'app/stores/StoreContext'
import { assertGood } from 'lib/assertBoom'
import { debug, logWarn } from 'lib/logging'

function parseSettingValue(value: string | undefined): UserSetting[`value`] {
  if (typeof value === `undefined` || value === ``) return undefined
  try {
    // A cleared setting is stored as the JSON literal `null`, which readers expect to see
    // as "no value" rather than an explicit null.
    return JSON.parse(value) ?? undefined
  } catch (err) {
    logWarn(`Failed to parse user setting value`, err)
    return value
  }
}

export function useSettings<T = UserSetting[`value`]>(
  key: string,
): [T | undefined, (key: string, value: T | undefined) => Promise<T | undefined>, boolean, boolean] {
  const store = useUserSettingStore()
  const [userSettings, setUserSettings] = useState<T>()
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  // A failed read also ends loading, with no value. Callers that would save a default
  // over an empty value must check this, or they overwrite what the user saved.
  const [readFailed, setReadFailed] = useState(false)
  const pendingRef = useRef<{
    timer: NodeJS.Timeout
    value: T | undefined
    resolve: (v: T | undefined) => void
  } | null>(null)
  debug(`userSettings`, `useSettings - hook`, key, store.settings[key])

  useEffect(() => {
    let cancelled = false
    setSettingsLoaded(false)
    setReadFailed(false)
    store.get(key).then((value) => {
      if (cancelled) return
      setUserSettings(parseSettingValue(value) as T | undefined)
      // The store records a key only when the API answered.
      setReadFailed(!(key in store.settings))
      setSettingsLoaded(true)
    })
    // The store is the shared source of truth, so every hook instance holding this
    // key follows it. Without this, a component that saves updates only its own
    // copy and every other reader of the same key keeps the value it read on
    // mount until the page reloads.
    const dispose = reaction(
      () => store.settings[key],
      (value) => {
        if (cancelled) return
        setUserSettings(parseSettingValue(value) as T | undefined)
      },
    )
    return () => {
      cancelled = true
      dispose()
    }
  }, [key, store])

  async function setSettings(settingKey: string, value: T | undefined): Promise<T | undefined> {
    debug([`user`, `userSettings`], `set`, settingKey, value, store.settings[settingKey])
    assertGood(settingKey, `settings key is required`)

    // Update local state immediately to trigger re-render
    if (settingKey === key) {
      setUserSettings(value)
    }

    // Cancel any pending debounced request and update with new value
    if (pendingRef.current) {
      clearTimeout(pendingRef.current.timer)
      pendingRef.current.resolve(pendingRef.current.value)
    }

    return new Promise((resolve) => {
      pendingRef.current = {
        value,
        resolve,
        timer: setTimeout(async () => {
          pendingRef.current = null
          if (value == null) {
            // An empty string is not valid JSON, and the API rejects it ("value must be
            // valid JSON") exactly like it rejected the bare strings handled below. The
            // store then rolls its optimistic write back, so clearing a setting appeared
            // to work for a moment and then reverted. `null` is valid JSON and clears it.
            await store.save(settingKey, `null`)
            resolve(undefined)
            return
          }
          assertGood(typeof value !== `undefined` && value !== null, `settings value is required`)
          if (isEqual(parseSettingValue(store.settings[settingKey]), value)) {
            debug([`user`, `userSettings`], `skipped save (equal)`, settingKey)
            resolve(value)
            return
          }
          // Always JSON, never a bare string. The API rejects a value that is not
          // valid JSON ("value must be valid JSON"), so passing a string through
          // raw made every string-valued setting fail to save while the caller saw
          // its optimistic local update and assumed success. parseSettingValue
          // still falls back to the raw string on read, so rows written before
          // this keep working.
          const serialized = JSON.stringify(value)
          debug([`user`, `userSettings`], `saving to server`, settingKey, serialized.slice(0, 100))
          await store.save(settingKey, serialized)
          resolve(value)
        }, 500),
      }
    })
  }

  return [userSettings, setSettings, settingsLoaded, readFailed]
}
